import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import { HardHat, Download, ClockCounterClockwise } from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';

export function InspectionEntrepriseForm({
  chantiers, inspChantier, setInspChantier,
  inspEntreprise, setInspEntreprise, inspRepresentant, setInspRepresentant,
  inspZone, setInspZone, inspTravaux, setInspTravaux,
  inspRisques, setInspRisques, inspNC, setInspNC,
  inspMesures, setInspMesures, inspObs, setInspObs,
  inspGenerating, inspEntreprises,
  inspHistory, inspHistoryOpen, setInspHistoryOpen,
  onGenerate,
}) {
  return (
    <div>
      <h2 className="text-base font-semibold text-[#111827] mb-3" data-testid="section-inspection">Inspection Commune — Entreprise</h2>
      <Card className="border-l-4 border-l-red-500" data-testid="inspection-entreprise-card">
        <CardHeader className="pb-3">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-500/10 flex items-center justify-center">
                <HardHat size={20} className="text-red-500" />
              </div>
              <div>
                <CardTitle className="text-base">Inspection terrain par entreprise</CardTitle>
                <p className="text-xs text-[#6B7280] mt-0.5">Remplissez les champs ci-dessous et généréz le PDF pret a signer</p>
              </div>
            </div>
            <span className="text-[10px] font-bold text-red-500 bg-red-500/10 px-2 py-1 rounded">TERRAIN</span>
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Row 1: Chantier + Entreprise + Representant */}
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Chantier *</Label>
              <Select value={inspChantier} onValueChange={setInspChantier}>
                <SelectTrigger className="h-10" data-testid="insp-chantier-select">
                  <SelectValue placeholder="Sélectionner" />
                </SelectTrigger>
                <SelectContent position="popper" className="z-[100]">
                  {(chantiers || []).filter(c => c && c.id && c.name).map(c => (
                    <SelectItem key={c.id} value={String(c.id)}>{c.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Entreprise inspectee *</Label>
              {inspEntreprises.length > 0 ? (
                <Select value={inspEntreprise || '_manual'}
                  onValueChange={(v) => {
                    if (v === '_manual') { setInspEntreprise(''); setInspRepresentant(''); }
                    else {
                      const ent = inspEntreprises.find(e => e.nom === v);
                      setInspEntreprise(v);
                      if (ent?.representant) setInspRepresentant(ent.representant);
                    }
                  }}>
                  <SelectTrigger className="h-10" data-testid="insp-entreprise-select">
                    <SelectValue placeholder="Sélectionner ou saisir" />
                  </SelectTrigger>
                  <SelectContent position="popper" className="z-[100]">
                    <SelectItem value="_manual" className="py-2 text-[#6B7280]">Saisie manuelle</SelectItem>
                    {inspEntreprises.map(e => (
                      <SelectItem key={e.id} value={e.nom} className="py-2">{e.nom}{e.corps_etat ? ` (${e.corps_etat})` : ''}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <Input value={inspEntreprise} onChange={e => setInspEntreprise(e.target.value)}
                  placeholder="Ex: Martin BTP" className="h-10" data-testid="insp-entreprise" />
              )}
              {inspEntreprises.length > 0 && inspEntreprise === '' && (
                <Input value={inspEntreprise} onChange={e => setInspEntreprise(e.target.value)}
                  placeholder="Ou saisir manuellement" className="h-10 mt-1" data-testid="insp-entreprise-manual" />
              )}
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Representant *</Label>
              <Input value={inspRepresentant} onChange={e => setInspRepresentant(e.target.value)}
                placeholder="Ex: M. Dupont" className="h-10" data-testid="insp-representant" />
            </div>
          </div>

          {/* Row 2: Zone + Travaux */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Zone d'intervention</Label>
              <Input value={inspZone} onChange={e => setInspZone(e.target.value)}
                placeholder="Ex: Batiment A - 2eme etage" className="h-10" data-testid="insp-zone" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Travaux concernes</Label>
              <Input value={inspTravaux} onChange={e => setInspTravaux(e.target.value)}
                placeholder="Ex: Pose cloisons, electricite" className="h-10" data-testid="insp-travaux" />
            </div>
          </div>

          {/* Row 3: Risques + NC */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Risques identifies</Label>
              <textarea value={inspRisques} onChange={e => setInspRisques(e.target.value)}
                placeholder="Chute de hauteur, risque electrique..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="insp-risques" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Non-conformites constatees</Label>
              <textarea value={inspNC} onChange={e => setInspNC(e.target.value)}
                placeholder="Absence EPI, echafaudage non conforme..."
                className="w-full rounded-md border border-red-300 bg-red-50/50 px-3 py-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-2 focus:ring-red-400"
                data-testid="insp-nc" />
            </div>
          </div>

          {/* Row 4: Mesures + Observations */}
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Mesures correctives demandees</Label>
              <textarea value={inspMesures} onChange={e => setInspMesures(e.target.value)}
                placeholder="Port du harnais obligatoire, verification echafaudage..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="insp-mesures" />
            </div>
            <div>
              <Label className="text-xs text-[#6B7280] mb-1 block">Observations</Label>
              <textarea value={inspObs} onChange={e => setInspObs(e.target.value)}
                placeholder="Remarques complementaires..."
                className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm min-h-[70px] resize-y focus:outline-none focus:ring-2 focus:ring-ring"
                data-testid="insp-observations" />
            </div>
          </div>

          {/* Action buttons */}
          <div className="flex flex-col sm:flex-row items-center gap-3 pt-2">
            <Button onClick={onGenerate}
              disabled={!inspChantier || !inspEntreprise.trim() || !inspRepresentant.trim() || inspGenerating}
              className="w-full sm:w-auto h-11 bg-red-600 hover:bg-red-700 px-8" data-testid="insp-generate-btn">
              {inspGenerating ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Download size={18} className="mr-2" />}
              {inspGenerating ? 'Génération...' : 'Générer le PDF Inspection'}
            </Button>
            {inspChantier && inspHistory.length > 0 && (
              <Button variant="outline" onClick={() => setInspHistoryOpen(!inspHistoryOpen)}
                className="w-full sm:w-auto h-11" data-testid="insp-history-toggle">
                <ClockCounterClockwise size={18} className="mr-2" />
                Historique ({inspHistory.length})
              </Button>
            )}
          </div>

          {/* History */}
          {inspHistoryOpen && inspHistory.length > 0 && (
            <div className="border-t pt-3 mt-2 space-y-2" data-testid="insp-history-list">
              <p className="text-xs font-medium text-[#6B7280]">Inspections precedentes</p>
              {inspHistory.map((h) => (
                <div key={h.id || h.created_at} className="flex items-center justify-between bg-[#F9FAFB] rounded-lg px-3 py-2 text-sm">
                  <div>
                    <span className="font-medium text-[#111827]">{h.entreprise}</span>
                    <span className="text-[#6B7280] mx-2">—</span>
                    <span className="text-[#6B7280]">{h.representant}</span>
                    {h.reference && <span className="text-xs text-[#9CA3AF] ml-2">{h.reference}</span>}
                  </div>
                  <span className="text-xs text-[#9CA3AF]">
                    {h.created_at ? new Date(h.created_at).toLocaleDateString('fr-FR') : ''}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
